import React, { useEffect, useState } from 'react';
import {
  AppLayout,
  SpaceBetween,
  Container,
  Header,
  Box,
  Button,
  Alert,
  ColumnLayout,
} from '@cloudscape-design/components';
import TopPanel from '../TopPanel';
import ServiceNavigation from '../ServiceNavigation.jsx';
import SynopsisPanelV3 from '../SynopsisPanelV3';
import ExtendingPanel from '../ExtendingPanel.jsx';
import { sendAnalytics } from '../../resources/rum-provider';
import { NETWORKING_ARCHITECTURE_BUILDER_ENDPOINT } from '../../resources/prod-env.jsx';
import architectureImg from './images/architecture.png';

const demoModule = "networking-architecture-builder";

// The tool is served by this demo's own stack (backend/networking-architecture-builder):
// private S3 + OAC behind its own CloudFront distribution. deploy.sh writes the base URL
// into prod-env.jsx; empty means the stack has not been deployed yet.
const toolBase = (NETWORKING_ARCHITECTURE_BUILDER_ENDPOINT || '').replace(/\/+$/, '');
const toolUrl = toolBase ? `${toolBase}/index.html` : '';

export default function NetworkingBuilder() {
  // Start with the side nav collapsed so the tool gets the full width; the menu button reopens it.
  const [navOpen, setNavOpen] = useState(false);
  // Usage ping on mount - the demo was opened (portal-wide convention).
  useEffect(() => {
    sendAnalytics({ demo: demoModule });
  }, []);

  const openFullScreen = () => {
    sendAnalytics({ demo: demoModule, action: 'open-full-screen' });
    window.open(toolUrl, '_blank', 'noopener,noreferrer');
  };

  return (
    <React.Fragment>
      <TopPanel label="Networking Architecture Builder" />
      <AppLayout
        navigation={<ServiceNavigation />}
        navigationOpen={navOpen}
        onNavigationChange={({ detail }) => setNavOpen(detail.open)}
        maxContentWidth={Number.MAX_VALUE}
        toolsHide
        content={
          <SpaceBetween size="m">
            <SynopsisPanelV3 label="Synopsis" demotype="demo" architectureImgs={[architectureImg]}>
              An interactive, drag-and-drop tool for designing AWS network architectures with{' '}
              <b>phased service recommendations</b>. Start from the guided <b>Build Wizard</b>, a
              pre-built template, or a blank canvas; then generate a recommended design across the
              foundation (Direct Connect, CloudFront, Route 53), the network overlay (Cloud WAN,
              Transit Gateway, Network Firewall) and service networking (VPC Lattice, Verified
              Access, PrivateLink), with a Well-Architected scorecard, a cost estimate and PDF
              export. The tool runs entirely in the browser. This portal copy is the customer
              build: it contains only content that is safe to share with customers.
            </SynopsisPanelV3>

            <Container
              header={
                <Header
                  variant="h2"
                  actions={
                    <Button iconName="external" iconAlign="right" disabled={!toolUrl} onClick={openFullScreen}>
                      Open full screen
                    </Button>
                  }
                >
                  Networking Architecture Builder
                </Header>
              }
            >
              {toolUrl ? (
                <iframe
                  src={toolUrl}
                  title="Networking Architecture Builder"
                  allow="clipboard-read; clipboard-write; fullscreen"
                  style={{ display: 'block', width: '100%', height: 'calc(100vh - 180px)', minHeight: 720, border: 'none' }}
                />
              ) : (
                <Alert type="info" header="Not deployed yet">
                  Run <code>backend/networking-architecture-builder/deploy.sh</code> to publish the
                  tool; it writes <code>NETWORKING_ARCHITECTURE_BUILDER_ENDPOINT</code> into
                  prod-env.jsx.
                </Alert>
              )}
            </Container>

            <ExtendingPanel label="Extend it">
              <ColumnLayout columns={2} variant='text-grid' className='extend_it'>
                <Box>Questions/ideas on this demo?</Box>
                <Box variant='strong'>
                  Mark Levitan, mlevita@amazon.com
                </Box>
              </ColumnLayout>
            </ExtendingPanel>
          </SpaceBetween>
        }
        contentType="default"
      />
    </React.Fragment>
  );
}
